import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modalViewsConst, setModalOpen, setModalState } from "../store/modalSlice";
import type { ModalState } from "../store/modalSlice";
import type { RootState } from "../store";

interface LinkedModalOptions<T> {
  /** The URL's link as a stable key ("dataset:<id>", or just the id), or null when there is none */
  linkKey: string | null;
  /** This visit's list has been read successfully, so a link not in it really is missing */
  ready: boolean;
  /** The linked item from the loaded list, or null */
  find: () => T | null;
  /** Opens the item's modal */
  open: (item: T) => void;
  /** Whether the global modal is showing the item behind this key */
  isShowing: (modal: ModalState, key: string) => boolean;
  /** Removes the link from the URL */
  clearLink: () => void;
  /** The notice for a link to something deleted or not shared with the viewer */
  notFound: { header: string; details: string };
}

/** Keeps a page's ?param=<id> link and the global modal in step: open once the list is read, drop the link on close, close on Back, report a missing item once. */
export const useLinkedModal = <T>({ linkKey, ready, find, open, isShowing, clearLink, notFound }: LinkedModalOptions<T>): void => {
  const dispatch = useDispatch();
  const modal = useSelector((state: RootState) => state.modalState);
  const openedKey = useRef<string | null>(null);
  // The notice modal re-renders before the cleared URL lands, so a missing link is reported once, not in a loop
  const missingKey = useRef<string | null>(null);
  // Callbacks are new every render; the effect reads the latest without re-running on them
  const latest = useRef({ find, open, isShowing, clearLink, notFound, modal });
  latest.current = { find, open, isShowing, clearLink, notFound, modal };

  // Leaving the page (Back to another route, a sidebar link) closes the modal it opened, or it would stay over the next page
  useEffect(
    () => () => {
      const opened = openedKey.current;
      if (opened && latest.current.isShowing(latest.current.modal, opened)) dispatch(setModalOpen(false));
    },
    [dispatch]
  );

  useEffect(() => {
    const { find: findItem, open: openItem, isShowing: shows, clearLink: clear, notFound: notice } = latest.current;
    const opened = openedKey.current;
    if (opened) {
      const showing = shows(modal, opened);
      if (linkKey === opened) {
        if (showing) return;
        openedKey.current = null;
        clear();
        return;
      }
      openedKey.current = null;
      if (showing) dispatch(setModalOpen(false));
    }
    if (!linkKey) {
      missingKey.current = null;
      return;
    }
    if (!ready || missingKey.current === linkKey) return;
    const item = findItem();
    if (!item) {
      missingKey.current = linkKey;
      clear();
      dispatch(setModalState({ modalOpen: true, modalView: modalViewsConst.notification, properties: { __modalSize: "sm", ...notice } }));
      return;
    }
    openedKey.current = linkKey;
    openItem(item);
  }, [linkKey, ready, modal, dispatch]);
};
